import { get, set, del } from 'idb-keyval';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';

const IDB_KEY = 'REACT_QUERY_OFFLINE_CACHE';
const USER_PHOTO_KEY = 'UserPhoto';
const USER_PHOTO_TS_KEY = 'UserPhotoTS';
const TECHNICIAN_SKILL_PREFIX = 'TECHNICIAN_SKILL_CACHE_V1_';
export const TECHNICIAN_SKILL_CACHE_MAX_AGE = 30 * 60 * 1000;

/**
 * IndexedDB tabanlı AsyncStorage adaptörü
 * localStorage'ın 5MB limitini aşan büyük combo verileri için kullanılır.
 */
const indexedDBStorage = {
  getItem: async (key) => {
    const value = await get(key);
    return value ?? null;
  },
  setItem: async (key, value) => {
    await set(key, value);
  },
  removeItem: async (key) => {
    await del(key);
  },
};

// =============================================
// 🖼️ UserPhoto IndexedDB Helpers
// =============================================

/** Kullanıcı fotoğrafını IndexedDB'den oku */
export const getUserPhoto = async () => {
  try {
    const value = await get(USER_PHOTO_KEY);
    return value ?? null;
  } catch (e) {
    console.warn('[IndexedDB] UserPhoto okuma hatası:', e);
    return null;
  }
};

/** Kullanıcı fotoğrafını IndexedDB'ye yaz */
export const setUserPhoto = async (photo) => {
  try {
    await set(USER_PHOTO_KEY, photo);
    await set(USER_PHOTO_TS_KEY, String(Date.now()));
  } catch (e) {
    console.warn('[IndexedDB] UserPhoto yazma hatası:', e);
  }
};

/** Servis mağazasına ait teknisyen uzmanlıklarını süre kontrolüyle IndexedDB'den oku. */
export const getTechnicianSkillCache = async (
  serviceShopId,
  maxAge = TECHNICIAN_SKILL_CACHE_MAX_AGE,
) => {
  const cacheKey = `${TECHNICIAN_SKILL_PREFIX}${String(serviceShopId || 'default')}`;
  try {
    const cached = await get(cacheKey);
    if (!cached || !Array.isArray(cached.data) || !Number.isFinite(cached.savedAt)) return null;
    if (Date.now() - cached.savedAt >= maxAge) {
      await del(cacheKey);
      return null;
    }
    return cached.data;
  } catch (e) {
    console.warn('[IndexedDB] Teknisyen uzmanlık cache okuma hatası:', e);
    return null;
  }
};

/** Servis mağazasına ait teknisyen uzmanlıklarını IndexedDB'ye kaydet. */
export const setTechnicianSkillCache = async (serviceShopId, data) => {
  const cacheKey = `${TECHNICIAN_SKILL_PREFIX}${String(serviceShopId || 'default')}`;
  try {
    await set(cacheKey, { data, savedAt: Date.now() });
  } catch (e) {
    console.warn('[IndexedDB] Teknisyen uzmanlık cache yazma hatası:', e);
  }
};

/** Uzmanlık ataması değiştiğinde ilgili servis mağazasının cache kaydını sil. */
export const removeTechnicianSkillCache = async (serviceShopId) => {
  const cacheKey = `${TECHNICIAN_SKILL_PREFIX}${String(serviceShopId || 'default')}`;
  try {
    await del(cacheKey);
  } catch (e) {
    console.warn('[IndexedDB] Teknisyen uzmanlık cache silme hatası:', e);
  }
};

/** Kullanıcı fotoğrafını IndexedDB'den sil */
export const removeUserPhoto = async () => {
  try {
    await del(USER_PHOTO_KEY);
    await del(USER_PHOTO_TS_KEY);
  } catch (e) {
    console.warn('[IndexedDB] UserPhoto silme hatası:', e);
  }
};

/** Kullanıcı fotoğrafı timestamp'ini IndexedDB'den oku */
export const getUserPhotoTS = async () => {
  try {
    const value = await get(USER_PHOTO_TS_KEY);
    return value ?? null;
  } catch (e) {
    console.warn('[IndexedDB] UserPhotoTS okuma hatası:', e);
    return null;
  }
};

/**
 * İlk yüklemede localStorage'daki mevcut cache'i IndexedDB'ye taşır,
 * ardından localStorage'dan siler.
 */
export async function migrateLocalStorageToIndexedDB() {
  try {
    // React Query cache migration
    const existing = localStorage.getItem(IDB_KEY);
    if (existing) {
      await set(IDB_KEY, existing);
      localStorage.removeItem(IDB_KEY);
      console.info('[Cache Migration] REACT_QUERY_OFFLINE_CACHE: localStorage → IndexedDB taşıma tamamlandı.');
    }

    // UserPhoto migration
    const photo = localStorage.getItem(USER_PHOTO_KEY);
    const photoTS = localStorage.getItem(USER_PHOTO_TS_KEY);
    if (photo !== null) {
      await set(USER_PHOTO_KEY, photo);
      localStorage.removeItem(USER_PHOTO_KEY);
    }
    if (photoTS !== null) {
      await set(USER_PHOTO_TS_KEY, photoTS);
      localStorage.removeItem(USER_PHOTO_TS_KEY);
    }
    if (photo !== null || photoTS !== null) {
      console.info('[Cache Migration] UserPhoto: localStorage → IndexedDB taşıma tamamlandı.');
    }

    // Redux persist:root migration
    const persistRoot = localStorage.getItem('persist:root');
    if (persistRoot) {
      await set('persist:root', persistRoot);
      localStorage.removeItem('persist:root');
      console.info('[Cache Migration] persist:root: localStorage → IndexedDB taşıma tamamlandı.');
    }
  } catch (e) {
    console.warn('[Cache Migration] Taşıma sırasında hata:', e);
  }
}

// =============================================
// 📦 Combo Preload IndexedDB Helpers
// =============================================

const COMBO_PREFIX = 'COMBO_CACHE_';
const COMBO_TS_PREFIX = 'COMBO_CACHE_TS_';

/**
 * Combo verisinin süresinin dolup dolmadığını kontrol eder.
 * Gece 00:00'da sıfırlanır.
 */
const isComboExpired = (savedTimestamp) => {
  if (!savedTimestamp) return true;
  const ts = typeof savedTimestamp === 'string' ? parseInt(savedTimestamp, 10) : savedTimestamp;
  const savedDate = new Date(ts);
  const today = new Date();
  // Farklı güne geçildiyse (gece yarısı aşıldıysa) expire olmuştur
  return savedDate.toDateString() !== today.toDateString();
};

/**
 * Combo verisini IndexedDB'den oku (cache key: tableName + filter + idField + textField)
 */
export const getComboCache = async (cacheKey) => {
  try {
    const ts = await get(COMBO_TS_PREFIX + cacheKey);
    if (isComboExpired(ts)) {
      // Süresi dolmuş, temizle
      await del(COMBO_PREFIX + cacheKey);
      await del(COMBO_TS_PREFIX + cacheKey);
      return null;
    }
    const value = await get(COMBO_PREFIX + cacheKey);
    return value ?? null;
  } catch (e) {
    console.warn('[IndexedDB] Combo cache okuma hatası:', cacheKey, e);
    return null;
  }
};

/**
 * Combo verisini IndexedDB'ye yaz (gece 00:00'da geçersiz olur)
 */
export const setComboCache = async (cacheKey, data) => {
  try {
    await set(COMBO_PREFIX + cacheKey, data);
    await set(COMBO_TS_PREFIX + cacheKey, String(Date.now()));
  } catch (e) {
    console.warn('[IndexedDB] Combo cache yazma hatası:', cacheKey, e);
  }
};

/**
 * Tüm combo cache'ini temizle
 */
export const clearAllComboCache = async () => {
  try {
    const { keys } = await import('idb-keyval');
    const allKeys = await keys();
    const comboKeys = allKeys.filter(k =>
      (typeof k === 'string') && (k.startsWith(COMBO_PREFIX) || k.startsWith(COMBO_TS_PREFIX))
    );
    for (const key of comboKeys) {
      await del(key);
    }
    console.info('[IndexedDB] Tüm combo cache temizlendi.');
  } catch (e) {
    console.warn('[IndexedDB] Combo cache temizleme hatası:', e);
  }
};

/**
 * Combo cache key oluşturur (tableName + filter + idField + textField)
 */
export const buildComboCacheKey = (tableName, filter, idField, textField) => {
  return `${tableName}|${filter || ''}|${idField || 'Id'}|${textField || 'Name'}`;
};

/**
 * IndexedDB destekli async persister oluşturur.
 * maxAge: 24 saat (gece 24'e kadar cache)
 */
export const indexedDBPersister = createAsyncStoragePersister({
  storage: indexedDBStorage,
  key: IDB_KEY,
  throttleTime: 1000,
});
